import React, { useState } from 'react';
import { Box, Menu, MenuItem } from '@mui/material';
import Check from '@mui/icons-material/Check';
import { ArcIconButton, ArcText, ArcTooltip } from '@src/core/ui';
import { axios } from '@src/service/get-axios';
import { trackError, toTrackedError } from '@src/core/sentry';
import { VideoQualityIcon } from './icons';
import { CAMERA_TOOLBAR_ICON_BUTTON_SX } from './toolbar-icon-button-sx';
import {
  cameraMenuDropdownCaptionSx,
  cameraMenuDropdownMenuItemSx,
  cameraMenuDropdownPaperSx,
} from './camera-menu-dropdown-sx';

type QualityLevel = 'high' | 'medium' | 'low';

const MENU_LEVELS: { level: QualityLevel; label: string }[] = [
  { level: 'high', label: 'High' },
  { level: 'medium', label: 'Medium' },
  { level: 'low', label: 'Low' },
];

interface VideoQualitySelectorProps {
  serialNumber: string;
}

const VideoQualitySelector: React.FC<VideoQualitySelectorProps> = ({ serialNumber }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [active, setActive] = useState<QualityLevel>('high');
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);

  const handleSelect = async (level: QualityLevel) => {
    if (level === active || loading) {
      setAnchorEl(null);
      return;
    }
    setLoading(true);
    try {
      await axios.post('/api/v1/sfu/video-quality', {
        droneId: serialNumber,
        level,
      });
      setActive(level);
    } catch (e) {
      trackError(toTrackedError(e), {
        code: 'FE_0001',
        extra: { flow: 'sfuVideoQuality', droneId: serialNumber, level },
        fingerprint: ['FE_0001', 'sfu/video-quality'],
      });
      console.error('Failed to set video quality', e);
    } finally {
      setLoading(false);
      setAnchorEl(null);
    }
  };

  return (
    <>
      <ArcTooltip title="Video Quality">
        <ArcIconButton
          disabled={loading}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={CAMERA_TOOLBAR_ICON_BUTTON_SX}>
          <VideoQualityIcon />
        </ArcIconButton>
      </ArcTooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: cameraMenuDropdownPaperSx,
          },
        }}>
        <Box
          role="presentation"
          sx={{
            px: 1.25,
            pb: 0.5,
            textAlign: 'center',
            pointerEvents: 'none',
          }}>
          <ArcText.caption sx={cameraMenuDropdownCaptionSx}>Video Quality</ArcText.caption>
        </Box>
        {MENU_LEVELS.map(({ level, label }) => (
          <MenuItem
            key={level}
            selected={active === level}
            disabled={loading}
            onClick={() => handleSelect(level)}
            sx={cameraMenuDropdownMenuItemSx}>
            <Box sx={{ width: 10, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
              {active === level ? <Check sx={{ fontSize: 14, color: 'common.white' }} /> : null}
            </Box>
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default VideoQualitySelector;
